import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit, Calendar, ChevronLeft, ChevronRight, Trash2, CalendarDays, RefreshCw, Users, Building2, Palmtree, ClipboardList, CheckCircle2, Megaphone, Copy, Wand2, BarChart3, FileSpreadsheet, FileText, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Tutti gli endpoint turni richiedono il prefisso /api
const TURNI_API = `${API}/api`;

// TURNI HARDCODED
const TURNI_DEFAULT = [
  {id: "m", sigla: "M", descrizione: "Mattina", colore: "#fbbf24", icona: "☀️", durata_ore: 8, ordine: 1},
  {id: "p", sigla: "P", descrizione: "Pomeriggio", colore: "#fb923c", icona: "🌤️", durata_ore: 8, ordine: 2},
  {id: "n", sigla: "N", descrizione: "Notte", colore: "#3b82f6", icona: "🌙", durata_ore: 12, ordine: 3},
  {id: "pn", sigla: "P/N", descrizione: "Pomeriggio/Notte", colore: "#8b5cf6", icona: "🌆", durata_ore: 12, ordine: 4},
  {id: "rep", sigla: "REP", descrizione: "Reperibilità", colore: "#10b981", icona: "📱", durata_ore: 12, ordine: 5},
  {id: "r", sigla: "R", descrizione: "Riposo", colore: "#6b7280", icona: "🛌", durata_ore: 0, ordine: 6},
  {id: "f", sigla: "F", descrizione: "Ferie", colore: "#14b8a6", icona: "🏖️", durata_ore: 0, ordine: 7},
  {id: "mal", sigla: "MAL", descrizione: "Malattia", colore: "#ef4444", icona: "🤒", durata_ore: 0, ordine: 8},
  {id: "per", sigla: "PER", descrizione: "Permesso", colore: "#a855f7", icona: "📋", durata_ore: 0, ordine: 9}
];

export default function GestioneTurni({ user }) {
  const [legenda, setLegenda] = useState(TURNI_DEFAULT);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTurno, setEditingTurno] = useState(null);
  const [turnoForm, setTurnoForm] = useState({
    sigla: '', descrizione: '', colore: '#3b82f6', icona: '', durata_ore: 8, ordine: 0
  });
  
  // Calendario state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarioData, setCalendarioData] = useState({ users: [], assegnazioni: [] });
  const [selectedCell, setSelectedCell] = useState(null);
  const [showAssegnaDialog, setShowAssegnaDialog] = useState(false);
  const [viewMode, setViewMode] = useState('month'); // 'month' o 'week'
  const [draggedShift, setDraggedShift] = useState(null);
  
  // Dialog states
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [showAutoAssignDialog, setShowAutoAssignDialog] = useState(false);
  // showRichiestaDialog inizializzato in modo lazy piu' avanti per leggere sessionStorage
  const [isSubmittingRichiesta, setIsSubmittingRichiesta] = useState(false); // Previene doppio invio
  const [richiestaForm, setRichiestaForm] = useState({
    tipo: 'ferie',
    data_inizio: '',
    data_fine: '',
    ore: '',
    motivo: ''
  });
  
  // Richieste assenze
  const [richieste, setRichieste] = useState([]);
  const [showRichiesteDialog, setShowRichiesteDialog] = useState(false);
  const [showReportAssenzeDialog, setShowReportAssenzeDialog] = useState(false);
  const [reportAssenzeAnno, setReportAssenzeAnno] = useState(new Date().getFullYear());
  const [reportAssenzeMese, setReportAssenzeMese] = useState('');
  const [reportAssenzeAsl, setReportAssenzeAsl] = useState('');
  const [reportAssenzeLoading, setReportAssenzeLoading] = useState(false);
  const [filtroStatoRichieste, setFiltroStatoRichieste] = useState('pending'); // Default: da approvare
  
  // Traccia richieste già viste con localStorage
  const [richiesteViste, setRichiesteViste] = useState(() => {
    const saved = localStorage.getItem(`richiesteViste_${user.id}`);
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  
  // Filtro operatori
  const [filtroOperatore, setFiltroOperatore] = useState('tutti');
  
  // Filtro ASL (solo per super admin)
  const [filtroASL, setFiltroASL] = useState('');
  const [listaASL, setListaASL] = useState([]);
  
  // Statistiche turni
  const [statistiche, setStatistiche] = useState([]);
  const [showStatistiche, setShowStatistiche] = useState(false);

  // Salva richiesteViste in localStorage quando cambia
  useEffect(() => {
    localStorage.setItem(`richiesteViste_${user.id}`, JSON.stringify([...richiesteViste]));
  }, [richiesteViste, user.id]);

  // Marca richieste pending come viste — chiamato all'apertura del dialog (event handler, non in effect)
  const markPendingAsViewed = useCallback(() => {
    setRichiesteViste(prev => {
      const next = new Set(prev);
      let changed = false;
      richieste.forEach(r => {
        if (r.stato === 'pending' && !prev.has(r.id)) {
          next.add(r.id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [richieste]);

  // Apri automaticamente il dialog richiesta assenza se richiesto da altra pagina (lazy initial state)
  const [showRichiestaDialog, setShowRichiestaDialog] = useState(() => {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('openAbsenceDialog') === '1') {
      sessionStorage.removeItem('openAbsenceDialog');
      return true;
    }
    return false;
  });

  const isReadOnly = user.role === 'operatore' && !user.is_coordinatore;
  const canEditLegenda = user.role === 'super_amministratore';

  const fetchCalendario = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setCalendarioData({ users: [], assegnazioni: [] });
        return;
      }
      
      const anno = currentDate.getFullYear();
      const mese = currentDate.getMonth() + 1;
      
      const response = await axios.get(`${TURNI_API}/turni/calendario/${anno}/${mese}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCalendarioData(response.data || { users: [], assegnazioni: [] });
    } catch (error) {
      setCalendarioData({ users: [], assegnazioni: [] });
    }
  }, [currentDate]);
  
  const fetchRichieste = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${TURNI_API}/richieste-assenze`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRichieste(response.data);
    } catch (error) {
      console.error('Errore caricamento richieste:', error);
    }
  }, []);
  
  const fetchStatistiche = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const anno = currentDate.getFullYear();
      const mese = currentDate.getMonth() + 1;
      const response = await axios.get(`${TURNI_API}/turni/calendario/${anno}/${mese}/statistiche`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatistiche(response.data.statistiche);
    } catch (error) {
      console.error('Errore caricamento statistiche:', error);
    }
  }, [currentDate]);

  useEffect(() => {
    // Avvia i fetch in microtask separati cosi' i setState risultanti
    // non vengono considerati "set-state-in-effect" sincroni.
    queueMicrotask(() => fetchCalendario());
    queueMicrotask(() => fetchRichieste());
    if (!isReadOnly) {
      queueMicrotask(() => fetchStatistiche());
    }
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${TURNI_API}/turni/legenda`, {headers: {Authorization: `Bearer ${token}`}})
        .then(res => {
          if (res.data && res.data.length > 0) {
            setLegenda(res.data);
          }
        })
        .catch(() => {});
      
      if (user.role === 'super_amministratore') {
        axios.get(`${API}/api/asl`, {headers: {Authorization: `Bearer ${token}`}})
          .then(res => {
            if (res.data && res.data.length > 0) {
              setListaASL(res.data);
            }
          })
          .catch(() => {});
      }
    }
  }, [currentDate, fetchCalendario, fetchRichieste, fetchStatistiche, isReadOnly, user.role]);

  const resetForm = () => {
    setTurnoForm({sigla: '', descrizione: '', colore: '#3b82f6', icona: '', durata_ore: 8, ordine: 0});
  };

  const assegnaTurno = async (userId, data, turnoId) => {
    try {
      const token = localStorage.getItem('token');
      const [anno, mese, giorno] = data.split('-').map(Number);
      
      await axios.post(`${TURNI_API}/turni/calendario`, {
        id: `${userId}-${data}`,
        user_id: userId,
        turno_id: turnoId,
        data,
        anno,
        mese,
        giorno
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Turno assegnato');
      fetchCalendario();
      setShowAssegnaDialog(false);
    } catch (error) {
      toast.error('Errore assegnazione turno');
    }
  };

  const rimuoviTurno = async (assegnazioneId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${TURNI_API}/turni/calendario/${assegnazioneId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Turno rimosso');
      fetchCalendario();
    } catch (error) {
      toast.error('Errore rimozione turno');
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  // Genera array di giorni con celle vuote per iniziare da Lunedì
  const monthDaysWithOffset = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Primo giorno del mese (0 = Domenica, 1 = Lunedì, ..., 6 = Sabato)
    const firstDay = new Date(year, month, 1).getDay();
    
    // Converti: Domenica (0) diventa 6, Lunedì (1) diventa 0, etc.
    const firstDayOffset = firstDay === 0 ? 6 : firstDay - 1;
    
    const days = [];
    
    // Aggiungi celle vuote all'inizio
    for (let i = 0; i < firstDayOffset; i++) {
      days.push({ empty: true, index: i });
    }
    
    // Aggiungi i giorni del mese
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({ empty: false, day, index: firstDayOffset + day - 1 });
    }
    
    // Aggiungi celle vuote alla fine per completare la settimana
    const totalCells = days.length;
    const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
    for (let i = 0; i < remainingCells; i++) {
      days.push({ empty: true, index: totalCells + i });
    }
    
    return days;
  }, [currentDate]);

  const getTurnoForCell = (userId, giorno) => {
    const data = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(giorno).padStart(2, '0')}`;
    return calendarioData.assegnazioni.find(a => a.user_id === userId && a.data === data);
  };

  const getTurnoById = (turnoId) => {
    return legenda.find(t => t.id === turnoId);
  };

  const changeMonth = (delta) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + delta, 1));
  };

  const changeWeek = (delta) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (delta * 7));
    setCurrentDate(newDate);
  };

  const getWeekDays = () => {
    // Ottiene i 7 giorni della settimana corrente (Lun-Dom)
    const curr = new Date(currentDate);
    const first = curr.getDate() - curr.getDay() + 1; // Lunedì
    const days = [];
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(curr.setDate(first + i));
      days.push({
        date: day,
        day: day.getDate(),
        month: day.getMonth() + 1,
        year: day.getFullYear(),
        dateStr: `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`
      });
    }
    return days;
  };

  // Drag & Drop handlers
  const handleDragStart = (e, assegnazione) => {
    if (isReadOnly) return;
    setDraggedShift(assegnazione);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    if (isReadOnly) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, userId, data) => {
    if (isReadOnly) return;
    e.preventDefault();
    
    if (!draggedShift) return;
    
    // Se il turno viene spostato sulla stessa cella, non fare nulla
    if (draggedShift.user_id === userId && draggedShift.data === data) {
      setDraggedShift(null);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const [anno, mese, giorno] = data.split('-').map(Number);
      
      // Rimuovi il turno dalla posizione precedente
      await axios.delete(`${TURNI_API}/turni/calendario/${draggedShift.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Crea il turno nella nuova posizione
      await axios.post(`${TURNI_API}/turni/calendario`, {
        id: `${userId}-${data}`,
        user_id: userId,
        turno_id: draggedShift.turno_id,
        data,
        anno,
        mese,
        giorno,
        note: draggedShift.note
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Turno spostato');
      fetchCalendario();
    } catch (error) {
      toast.error('Errore nello spostamento del turno');
    } finally {
      setDraggedShift(null);
    }
  };

  const handleSaveTurno = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingTurno) {
        // Modifica turno esistente
        await axios.put(`${TURNI_API}/turni/legenda/${editingTurno.id}`, turnoForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Turno aggiornato');
      } else {
        // Nuovo turno
        const newTurno = {
          id: `turno_${Date.now()}`,
          ...turnoForm,
          ordine: parseInt(turnoForm.ordine) || 0,
          created_at: new Date().toISOString()
        };
        
        await axios.post(`${TURNI_API}/turni/legenda`, newTurno, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Turno salvato');
      }
      
      setShowDialog(false);
      setEditingTurno(null);
      resetForm();
      
      // Ricarica turni dal server
      const response = await axios.get(`${TURNI_API}/turni/legenda`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.length > 0) {
        setLegenda(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleDeleteTurno = async (turnoId) => {
    if (!window.confirm('Eliminare questo turno?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${TURNI_API}/turni/legenda/${turnoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Turno eliminato');
      
      // Ricarica turni
      const response = await axios.get(`${TURNI_API}/turni/legenda`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.length > 0) {
        setLegenda(response.data);
      }
    } catch (error) {
      toast.error('Errore eliminazione turno');
    }
  };

  const daysInMonth = getDaysInMonth();

  const filteredUsers = useMemo(() => {
    if (!calendarioData.users) return [];
    return calendarioData.users
      .filter(u => u.full_name && u.full_name.trim())
      .filter(u => !filtroASL || u.asl_riferimento === filtroASL);
  }, [calendarioData.users, filtroASL]);

  const displayedUsers = useMemo(() => {
    return filteredUsers.filter(u => filtroOperatore === 'tutti' || u.id === filtroOperatore);
  }, [filteredUsers, filtroOperatore]);

  const pendingNotifications = useMemo(() => {
    return richieste.filter(r =>
      (r.stato === 'approved' || r.stato === 'rejected') && !richiesteViste.has(r.id)
    );
  }, [richieste, richiesteViste]);

  return (
    <div className="space-y-6">
      {/* Calendario Mensile */}
      <Card className="border-0 shadow-lg" data-testid="turni-card">
        <CardHeader className="pb-4">
          {/* Title row */}
          <div className="flex items-center justify-between mb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="w-5 h-5 text-cyan-600" />
              {isReadOnly ? 'I Miei Turni' : 'Calendario Turni'}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchCalendario} className="text-gray-400 hover:text-cyan-600 hover:bg-cyan-50" data-testid="turni-refresh">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {/* Navigation bar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3 pb-4 border-b border-gray-100">
            {/* View toggle */}
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('month')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === 'month' ? 'bg-white text-cyan-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                <Calendar className="w-3.5 h-3.5" />
                Mese
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === 'week' ? 'bg-white text-cyan-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                <CalendarDays className="w-3.5 h-3.5" />
                Settimana
              </button>
            </div>

            {/* Month/Week navigation */}
            <div className="flex items-center gap-1 self-end sm:self-auto">
              <Button variant="ghost" size="sm" onClick={() => viewMode === 'month' ? changeMonth(-1) : changeWeek(-1)} className="h-8 w-8 p-0 hover:bg-gray-100 rounded-full shrink-0">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="font-semibold text-sm min-w-[100px] text-center text-gray-900 capitalize">
                {viewMode === 'month' 
                  ? currentDate.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
                  : `${getWeekDays()[0].date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })} - ${getWeekDays()[6].date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}`
                }
              </span>
              <Button variant="ghost" size="sm" onClick={() => viewMode === 'month' ? changeMonth(1) : changeWeek(1)} className="h-8 w-8 p-0 hover:bg-gray-100 rounded-full shrink-0">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Filters + Actions */}
          <div className="space-y-3 pt-3">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-2">
              {!isReadOnly && calendarioData.users && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg flex-1 min-w-0">
                  <Users className="w-4 h-4 text-gray-400 shrink-0" />
                  <select 
                    value={filtroOperatore}
                    onChange={(e) => setFiltroOperatore(e.target.value)}
                    data-testid="turni-filter-operator"
                    className="text-sm text-gray-700 bg-transparent border-none focus:ring-0 cursor-pointer flex-1 min-w-0">
                    <option value="tutti">Tutti gli operatori</option>
                    {filteredUsers.map(operatore => (
                      <option key={operatore.id} value={operatore.id}>{operatore.full_name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {user.role === 'super_amministratore' && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg flex-1 min-w-0">
                  <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
                  <select 
                    value={filtroASL}
                    onChange={(e) => { setFiltroASL(e.target.value); setFiltroOperatore('tutti'); }}
                    data-testid="turni-filter-asl"
                    className="text-sm text-gray-700 bg-transparent border-none focus:ring-0 cursor-pointer flex-1 min-w-0">
                    <option value="">Tutte le ASL</option>
                    {listaASL.map(asl => (
                      <option key={asl.id || asl.nome} value={asl.nome}>{asl.nome}</option>
                    ))}
                    {listaASL.length === 0 && calendarioData.users && 
                      [...new Set(calendarioData.users.filter(u => u.asl_riferimento).map(u => u.asl_riferimento))].map(asl => (
                        <option key={asl} value={asl}>{asl}</option>
                      ))
                    }
                  </select>
                </div>
              )}
            </div>

            {/* Action buttons - grid on mobile, flex on desktop */}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowRichiestaDialog(true)}
                data-testid="turni-btn-assenza"
                className="h-9 gap-1.5 text-xs font-medium border-orange-200 text-orange-600 hover:bg-orange-50 hover:border-orange-300 justify-center">
                <Palmtree className="w-3.5 h-3.5" />
                Assenza
              </Button>
              
              <Button 
                variant="outline"
                size="sm"
                onClick={() => {
                  const nuoveViste = new Set(richiesteViste);
                  richieste.filter(r => r.stato === 'approved' || r.stato === 'rejected')
                    .forEach(r => nuoveViste.add(r.id));
                  setRichiesteViste(nuoveViste);
                  setShowRichiesteDialog(true);
                }}
                data-testid="turni-btn-richieste"
                className="h-9 gap-1.5 text-xs font-medium border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 relative justify-center">
                <ClipboardList className="w-3.5 h-3.5" />
                Richieste
                {pendingNotifications.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center font-bold animate-pulse">
                    {pendingNotifications.length}
                  </span>
                )}
              </Button>
            </div>

            {/* Admin tools - grid on mobile */}
            {!isReadOnly && (
              <div className="grid grid-cols-3 sm:flex sm:flex-wrap gap-2 pt-1 border-t border-gray-100">
                {(user.is_coordinatore || user.role === 'super_amministratore' || user.role === 'amministratore') && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowRichiesteDialog(true)}
                    data-testid="turni-btn-gestisci"
                    className="h-9 gap-1.5 text-xs font-medium border-emerald-200 text-emerald-600 hover:bg-emerald-50 hover:border-emerald-300 relative justify-center">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Gestisci
                    {(() => {
                      const pendingCount = richieste.filter(r => 
                        r.stato === 'pending' && !richiesteViste.has(r.id)
                      ).length;
                      return pendingCount > 0 ? (
                        <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                          {pendingCount}
                        </span>
                      ) : null;
                    })()}
                  </Button>
                )}

                {user.role === 'super_amministratore' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowReportAssenzeDialog(true)}
                    data-testid="turni-btn-report-assenze"
                    className="h-9 gap-1.5 text-xs font-medium border-cyan-200 text-cyan-700 hover:bg-cyan-50 hover:border-cyan-300 justify-center">
                    <FileText className="w-3.5 h-3.5" />
                    Report Assenze
                  </Button>
                )}
                
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const anno = currentDate.getFullYear();
                      const mese = currentDate.getMonth() + 1;
                      const response = await axios.post(
                        `${TURNI_API}/turni/calendario/${anno}/${mese}/publish`, {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      toast.success(response.data.message);
                      fetchCalendario();
                    } catch (error) {
                      const detail = error.response?.data?.detail;
                      toast.error(Array.isArray(detail) ? detail.map(err => err.msg || err).join(', ') : detail || 'Errore pubblicazione turni');
                    }
                  }}
                  data-testid="turni-btn-pubblica"
                  className="h-9 gap-1.5 text-xs font-medium border-green-200 bg-green-50 text-green-700 hover:bg-green-100 hover:border-green-300 justify-center">
                  <Megaphone className="w-3.5 h-3.5" />
                  Pubblica
                </Button>
                
                <Button 
                  variant="outline" size="sm"
                  onClick={() => setShowCloneDialog(true)}
                  data-testid="turni-btn-clona"
                  className="h-9 gap-1.5 text-xs font-medium border-violet-200 text-violet-600 hover:bg-violet-50 hover:border-violet-300 justify-center">
                  <Copy className="w-3.5 h-3.5" />
                  Clona
                </Button>
                
                <Button 
                  variant="outline" size="sm"
                  onClick={() => setShowAutoAssignDialog(true)}
                  data-testid="turni-btn-auto"
                  className="h-9 gap-1.5 text-xs font-medium border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300 justify-center">
                  <Wand2 className="w-3.5 h-3.5" />
                  Auto
                </Button>
                
                {(user.is_coordinatore || user.role === 'super_amministratore' || user.role === 'amministratore') && (
                  <Button 
                    variant="outline" size="sm"
                    onClick={() => setShowStatistiche(!showStatistiche)}
                    data-testid="turni-btn-stats"
                    className={`h-9 gap-1.5 text-xs font-medium border-cyan-200 text-cyan-600 hover:bg-cyan-50 hover:border-cyan-300 justify-center ${showStatistiche ? 'bg-cyan-50 border-cyan-300' : ''}`}>
                    <BarChart3 className="w-3.5 h-3.5" />
                    Stats
                  </Button>
                )}

                <Button 
                  variant="outline" size="sm"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const anno = currentDate.getFullYear();
                      const mese = currentDate.getMonth() + 1;
                      let url = `${TURNI_API}/turni/calendario/${anno}/${mese}/export-excel`;
                      if (filtroASL) url += `?asl=${encodeURIComponent(filtroASL)}`;
                      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
                      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = blobUrl;
                      link.setAttribute('download', filtroASL ? `calendario_turni_${mese}_${anno}_${filtroASL.replace(/\s+/g, '_')}.xlsx` : `calendario_turni_${mese}_${anno}.xlsx`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success('Excel scaricato!');
                    } catch (error) { toast.error('Errore download Excel'); }
                  }}
                  data-testid="turni-btn-excel"
                  className="h-9 gap-1.5 text-xs font-medium text-gray-500 border-gray-200 hover:text-emerald-600 hover:bg-emerald-50 justify-center">
                  <FileSpreadsheet className="w-3.5 h-3.5" />
                  Excel
                </Button>
                
                <Button 
                  variant="outline" size="sm"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const anno = currentDate.getFullYear();
                      const mese = currentDate.getMonth() + 1;
                      let url = `${TURNI_API}/turni/calendario/${anno}/${mese}/export-pdf`;
                      if (filtroASL) url += `?asl=${encodeURIComponent(filtroASL)}`;
                      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
                      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = blobUrl;
                      link.setAttribute('download', filtroASL ? `calendario_turni_${mese}_${anno}_${filtroASL.replace(/\s+/g, '_')}.pdf` : `calendario_turni_${mese}_${anno}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success('PDF scaricato!');
                    } catch (error) { toast.error('Errore download PDF'); }
                  }}
                  data-testid="turni-btn-pdf"
                  className="h-9 gap-1.5 text-xs font-medium text-gray-500 border-gray-200 hover:text-red-600 hover:bg-red-50 justify-center">
                  <FileText className="w-3.5 h-3.5" />
                  PDF
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {calendarioData.users.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg mb-2">Nessun operatore da visualizzare</p>
              <p className="text-sm">
                {user.role === 'operatore' && !user.is_coordinatore 
                  ? 'I tuoi turni appariranno qui quando verranno assegnati' 
                  : user.is_coordinatore && user.role !== 'super_amministratore'
                  ? 'Nessun operatore nel tuo team. Contatta il Super Admin per configurare il team.'
                  : 'Nessun operatore nel sistema. Crea degli operatori dalla sezione Utenti.'}
              </p>
            </div>
          ) : (
            <>
              {/* STATISTICHE TURNI */}
              {showStatistiche && statistiche.length > 0 && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    Riepilogo Turni per Operatore
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {statistiche.map(stat => (
                      <Card key={stat.user_id} className="border-2 hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <h4 className="font-bold text-sm text-gray-900">{stat.full_name}</h4>
                              <p className="text-xs text-gray-600">{stat.figura_professionale}</p>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-indigo-600">{stat.totale_turni}</div>
                              <div className="text-[10px] text-gray-500">turni</div>
                            </div>
                          </div>
                          
                          {/* Breakdown turni */}
                          <div className="space-y-1 mb-3">
                            {Object.entries(stat.breakdown).map(([sigla, data]) => (
                              <div key={sigla} className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-3 h-3 rounded-full" 
                                    style={{ backgroundColor: data.colore }}
                                  />
                                  <span className="font-medium">{sigla}</span>
                                  <span className="text-gray-600">{data.descrizione}</span>
                                </div>
                                <span className="font-bold text-gray-900">{data.count}</span>
                              </div>
                            ))}
                          </div>
                          
                          {/* Footer */}
                          <div className="pt-3 border-t flex justify-between items-center text-xs">
                            <span className="text-gray-600">Ore totali:</span>
                            <span className="font-bold text-indigo-600">{stat.ore_totali}h</span>
                          </div>
                          <div className="flex justify-between items-center text-xs mt-1">
                            <span className="text-green-600">✓ Pubblicati: {stat.turni_pubblicati}</span>
                            <span className="text-yellow-600">⚠ Bozze: {stat.turni_bozza}</span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  {/* Riepilogo Totale */}
                  <div className="mt-4 p-3 bg-white rounded border border-gray-200 flex justify-around text-center">
                    <div>
                      <div className="text-2xl font-bold text-indigo-600">
                        {statistiche.reduce((sum, s) => sum + s.totale_turni, 0)}
                      </div>
                      <div className="text-xs text-gray-600">Turni Totali</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">
                        {statistiche.reduce((sum, s) => sum + s.turni_pubblicati, 0)}
                      </div>
                      <div className="text-xs text-gray-600">Pubblicati</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-yellow-600">
                        {statistiche.reduce((sum, s) => sum + s.turni_bozza, 0)}
                      </div>
                      <div className="text-xs text-gray-600">Bozze</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">
                        {statistiche.reduce((sum, s) => sum + s.ore_totali, 0)}h
                      </div>
                      <div className="text-xs text-gray-600">Ore Totali</div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* VISTA SETTIMANALE - Formato Card Verticale Migliorato */}
              {viewMode === 'week' && (
                <div className="space-y-4">
                  {displayedUsers.map((operatore, idx) => {
                      const today = new Date().toISOString().split('T')[0];
                      
                      return (
                        <Card key={operatore.id} className="border-2 hover:border-cyan-300 transition-all shadow-md">
                          <CardHeader className="pb-4 bg-white border-b border-gray-100">
                            <div className="flex justify-between items-center">
                              <div>
                                <h3 className="font-bold text-lg text-gray-900">{operatore.full_name}</h3>
                                <p className="text-sm text-gray-600 mt-1">{operatore.figura_professionale}</p>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-4 pb-4 px-2 sm:pt-6 sm:pb-6 sm:px-6">
                            <div className="grid grid-cols-7 gap-1 sm:gap-3">
                              {getWeekDays().map((day, dayIdx) => {
                                const assegnazione = calendarioData.assegnazioni.find(
                                  a => a.user_id === operatore.id && a.data === day.dateStr
                                );
                                const turno = assegnazione ? getTurnoById(assegnazione.turno_id) : null;
                                const dayOfWeek = day.date.getDay();
                                const isSunday = dayOfWeek === 0;
                                const isSaturday = dayOfWeek === 6;
                                const isToday = day.dateStr === today;
                                const weekdayName = day.date.toLocaleDateString('it-IT', { weekday: 'short' });
                                const capitalizedWeekday = weekdayName.charAt(0).toUpperCase() + weekdayName.slice(1);
                                
                                return (
                                  <div 
                                    key={day.dateStr}
                                    className={`flex flex-col rounded-lg sm:rounded-xl overflow-hidden border sm:border-2 transition-all ${
                                      isToday ? 'ring-2 sm:ring-4 ring-cyan-400 ring-opacity-50' : ''
                                    } ${
                                      isSunday ? 'bg-red-50 border-red-300' : 
                                      isSaturday ? 'bg-orange-50 border-orange-300' : 
                                      'bg-white border-gray-200'
                                    } ${!isReadOnly ? 'hover:shadow-lg' : ''}`}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, operatore.id, day.dateStr)}
                                    onClick={() => {
                                      if (!isReadOnly) {
                                        setSelectedCell({ 
                                          userId: operatore.id, 
                                          data: day.dateStr, 
                                          userName: operatore.full_name, 
                                          assegnazione 
                                        });
                                        setShowAssegnaDialog(true);
                                      }
                                    }}>
                                    {/* Header giorno - Compatto su mobile */}
                                    <div className={`text-center py-1 px-0.5 sm:py-2 sm:px-2 ${
                                      isToday ? 'bg-cyan-500' :
                                      isSunday ? 'bg-red-200' : 
                                      isSaturday ? 'bg-orange-200' : 
                                      'bg-gray-100'
                                    }`}>
                                      <div className={`text-[9px] sm:text-sm font-bold ${
                                        isToday ? 'text-white' :
                                        isSunday ? 'text-red-900' : 
                                        isSaturday ? 'text-orange-900' : 
                                        'text-gray-700'
                                      }`}>
                                        {capitalizedWeekday.substring(0, 2)}
                                      </div>
                                      <div className={`text-base sm:text-2xl font-extrabold mt-0.5 sm:mt-1 ${
                                        isToday ? 'text-white' :
                                        isSunday ? 'text-red-800' : 
                                        isSaturday ? 'text-orange-800' : 
                                        'text-gray-900'
                                      }`}>
                                        {day.day}
                                      </div>
                                    </div>
                                    
                                    {/* Turno - Compatto su mobile, normale su desktop */}
                                    {turno ? (
                                      <div 
                                        className={`w-full p-1.5 sm:p-3 text-center relative min-h-[80px] sm:min-h-[120px] flex flex-col justify-center items-center ${!isReadOnly ? 'cursor-move hover:opacity-90' : ''} transition-all`}
                                        style={{ 
                                          backgroundColor: turno.colore + '20',
                                        }}
                                        draggable={!isReadOnly}
                                        onDragStart={(e) => handleDragStart(e, assegnazione)}>
                                        {!assegnazione.published && !isReadOnly && (
                                          <div className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 bg-yellow-500 text-white text-[6px] sm:text-[8px] px-1 sm:px-2 py-0.5 rounded-full font-bold shadow-sm">
                                            BOZZA
                                          </div>
                                        )}
                                        <div className="text-xl sm:text-4xl mb-1 sm:mb-2 leading-none">{turno.icona}</div>
                                        <div className="text-[10px] sm:text-base font-extrabold leading-tight mb-0.5 sm:mb-1" style={{color: turno.colore}}>
                                          {turno.sigla}
                                        </div>
                                        <div className="hidden sm:block text-[11px] text-gray-700 leading-snug font-medium px-1 break-words">
                                          {turno.descrizione}
                                        </div>
                                        {assegnazione.ore && !isReadOnly ? (
                                          <div className="text-[10px] sm:text-[11px] font-semibold text-gray-600 mt-0.5">
                                            {assegnazione.ore}h
                                          </div>
                                        ) : null}
                                      </div>
                                    ) : (
                                      <div className={`w-full p-1.5 sm:p-3 border border-dashed sm:border-2 text-center min-h-[80px] sm:min-h-[120px] flex items-center justify-center ${!isReadOnly ? 'cursor-pointer hover:border-cyan-400 hover:bg-cyan-50' : ''} transition-all ${
                                        isSunday ? 'border-red-300 bg-red-50/30' : 
                                        isSaturday ? 'border-orange-300 bg-orange-50/30' : 
                                        'border-gray-300'
                                      }`}>
                                        <span className="text-gray-300 text-lg sm:text-2xl font-light">—</span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                </div>
              )}
              
              {/* VISTA MENSILE */}
              {viewMode === 'month' && (
                <>
                  {/* Visualizzazione Mobile - Lista Compatta */}
                  <div className="lg:hidden space-y-4">
                {displayedUsers.map((operatore) => (
                  <div key={operatore.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                    <div className="px-3 py-2 border-b border-gray-100 bg-white">
                      <div className="font-semibold text-sm text-gray-900">{operatore.full_name}</div>
                      <div className="text-[11px] text-gray-500">{operatore.figura_professionale}</div>
                    </div>
                    <div className="p-2">
                      <div className="grid grid-cols-7 gap-1">
                        {monthDaysWithOffset.map((dayInfo, idx) => {
                          if (dayInfo.empty) {
                            return (
                              <div key={`empty-${idx}`} className="aspect-square bg-gray-100 rounded border border-gray-200">
                              </div>
                            );
                          }
                          
                          const day = dayInfo.day;
                          const assegnazione = getTurnoForCell(operatore.id, day);
                          const turno = assegnazione ? getTurnoById(assegnazione.turno_id) : null;
                          const dayOfWeek = new Date(currentDate.getFullYear(), currentDate.getMonth(), day).getDay();
                          const isSunday = dayOfWeek === 0;
                          const isSaturday = dayOfWeek === 6;
                          const weekdayShort = new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toLocaleDateString('it-IT', { weekday: 'short' });
                          
                          return (
                            <div key={day} 
                              className={`aspect-square flex flex-col items-center justify-center rounded border-2 text-center relative
                                ${!isReadOnly ? 'cursor-pointer active:scale-95' : ''}
                                ${isSunday ? 'bg-red-100 border-red-300' : 
                                  isSaturday ? 'bg-orange-50 border-orange-200' : 
                                  'bg-white border-gray-200'}`}
                              style={turno ? { backgroundColor: turno.colore + '30', borderColor: turno.colore, borderWidth: '2px' } : {}}
                              onClick={() => {
                                if (!isReadOnly) {
                                  const data = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                  setSelectedCell({ userId: operatore.id, data, userName: operatore.full_name, assegnazione });
                                  setShowAssegnaDialog(true);
                                }
                              }}>
                              <div className={`text-[9px] font-semibold absolute top-0.5 left-0.5 flex items-center gap-0.5 ${
                                isSunday ? 'text-red-700' : 
                                isSaturday ? 'text-orange-600' : 
                                'text-gray-400'
                              }`}>
                                <span>{day}</span>
                                <span>{weekdayShort}</span>
                              </div>
                              {turno ? (
                                <div className="flex flex-col items-center mt-2">
                                  <span className="text-lg">{turno.icona}</span>
                                  <span className="text-[9px] font-bold mt-0.5" style={{color: turno.colore}}>
                                    {turno.sigla}
                                  </span>
                                  {assegnazione.ore && !isReadOnly ? (
                                    <span className="text-[9px] font-semibold text-gray-600 mt-0.5">
                                      {assegnazione.ore}h
                                    </span>
                                  ) : null}
                                </div>
                              ) : (
                                <div className="text-gray-300 text-xs">-</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Visualizzazione Desktop - Tabella Classica con Colonna Fissa */}
              <div className="hidden lg:block -mx-6 px-6">
                <div className="shadow ring-1 ring-black ring-opacity-5 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="sticky left-0 z-20 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900 min-w-[180px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] border-r-2 border-gray-300">
                            Operatore
                          </th>
                          {monthDaysWithOffset.map((dayInfo, idx) => {
                            if (dayInfo.empty) {
                              return (
                                <th key={`empty-${idx}`} scope="col" className="px-2 py-3 text-center text-xs font-semibold min-w-[65px] bg-gray-100">
                                </th>
                              );
                            }
                            
                            const day = dayInfo.day;
                            const dayOfWeek = new Date(currentDate.getFullYear(), currentDate.getMonth(), day).getDay();
                            const isSunday = dayOfWeek === 0;
                            const isSaturday = dayOfWeek === 6;
                            const weekdayName = new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toLocaleDateString('it-IT', { weekday: 'short' });
                            
                            return (
                              <th key={day} scope="col" className={`px-2 py-3 text-center text-xs font-semibold min-w-[65px] ${
                                isSunday ? 'bg-red-100 text-red-800 border-l-2 border-r-2 border-red-300' : 
                                isSaturday ? 'bg-orange-50 text-orange-700' : 
                                'text-gray-900'
                              }`}>
                                <div className="flex items-center justify-center gap-1 text-sm font-bold">
                                  <span>{day}</span>
                                  <span>{weekdayName}</span>
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {displayedUsers.map((operatore, idx) => (
                          <tr key={operatore.id} className={idx % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'}>
                            <td className="sticky left-0 z-10 whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] border-r-2 border-gray-300"
                              style={{backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb'}}>
                              <div className="flex flex-col">
                                <span className="font-semibold">{operatore.full_name}</span>
                                <span className="text-xs text-gray-500">{operatore.figura_professionale}</span>
                              </div>
                            </td>
                            {monthDaysWithOffset.map((dayInfo, idx) => {
                              if (dayInfo.empty) {
                                return (
                                  <td key={`empty-${idx}`} className="relative px-2 py-2 text-center text-xs whitespace-nowrap border-l bg-gray-100">
                                  </td>
                                );
                              }
                              
                              const day = dayInfo.day;
                              const assegnazione = getTurnoForCell(operatore.id, day);
                              const turno = assegnazione ? getTurnoById(assegnazione.turno_id) : null;
                              const dayOfWeek = new Date(currentDate.getFullYear(), currentDate.getMonth(), day).getDay();
                              const isSunday = dayOfWeek === 0;
                              const isSaturday = dayOfWeek === 6;
                              
                              const data = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                              
                              return (
                                <td key={day} 
                                  className={`relative px-2 py-2 text-center text-xs whitespace-nowrap border-l
                                    ${!isReadOnly ? 'cursor-pointer hover:ring-2 hover:ring-inset hover:ring-cyan-500 hover:z-10' : ''}
                                    ${isSunday ? 'bg-red-100/60 border-l-2 border-r-2 border-red-200' : 
                                      isSaturday ? 'bg-orange-50/50' : ''}`}
                                  style={turno ? { backgroundColor: turno.colore + '25' } : {}}
                                  onDragOver={handleDragOver}
                                  onDrop={(e) => handleDrop(e, operatore.id, data)}
                                  onClick={() => {
                                    if (!isReadOnly) {
                                      setSelectedCell({ userId: operatore.id, data, userName: operatore.full_name, assegnazione });
                                      setShowAssegnaDialog(true);
                                    }
                                  }}>
                                  {turno ? (
                                    <div 
                                      className="flex flex-col items-center justify-center gap-1 py-1 relative"
                                      draggable={!isReadOnly}
                                      onDragStart={(e) => handleDragStart(e, assegnazione)}
                                      title={!isReadOnly ? '🖱️ Trascina per spostare' : ''}>
                                      {!assegnazione.published && !isReadOnly && (
                                        <div className="absolute -top-1 -right-0 bg-yellow-500 text-white text-[8px] px-1 py-0.5 rounded-sm font-bold z-10">
                                          B
                                        </div>
                                      )}
                                      <span className="text-xl">{turno.icona}</span>
                                      <span className="font-bold text-xs" style={{color: turno.colore}}>
                                        {turno.sigla}
                                      </span>
                                      {assegnazione.ore && !isReadOnly ? (
                                        <span className="text-[10px] font-semibold text-gray-600">
                                          {assegnazione.ore}h
                                        </span>
                                      ) : null}
                                    </div>
                                  ) : null}
                                </td>
              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
                </>
              )}
              
              {/* Istruzioni */}
              {!isReadOnly && (
                <div className="mt-4 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-xs text-gray-500">
                    <strong className="hidden lg:inline">Desktop:</strong> 
                    <span className="hidden lg:inline"> Trascina i turni per spostarli &middot; </span>
                    Click sulla cella per assegnare o modificare un turno
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Legenda Turni */}
      <Card className="border-0 shadow-sm" data-testid="turni-legenda">
        <CardContent className="py-4 px-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Legenda</span>
            {canEditLegenda && (
              <Button onClick={() => { resetForm(); setEditingTurno(null); setShowDialog(true); }}
                size="sm" variant="ghost"
                className="h-6 gap-1 text-[11px] text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 ml-auto px-2">
                <Plus className="w-3 h-3" />
                Aggiungi
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {legenda
              .filter(turno => {
                if (!isReadOnly) return true;
                const turniOperatore = calendarioData.assegnazioni?.filter(
                  a => a.user_id === user.id && a.turno_id === turno.id
                ) || [];
                return turniOperatore.length > 0;
              })
              .map(turno => (
              <div key={turno.id} 
                className="group flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border bg-white hover:shadow-sm transition-all"
                style={{borderColor: turno.colore + '60'}}
                title={`${turno.descrizione} — ${turno.durata_ore}h`}>
                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{backgroundColor: turno.colore}} />
                <span className="text-xs font-semibold" style={{color: turno.colore}}>{turno.sigla}</span>
                <span className="text-[11px] text-gray-500 hidden sm:inline">{turno.descrizione}</span>
                {canEditLegenda && (
                  <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-1">
                    <button className="p-0.5 rounded hover:bg-gray-100"
                      onClick={() => { setTurnoForm(turno); setEditingTurno(turno); setShowDialog(true); }}>
                      <Edit className="w-2.5 h-2.5 text-gray-400" />
                    </button>
                    <button className="p-0.5 rounded hover:bg-red-50"
                      onClick={() => handleDeleteTurno(turno.id)}>
                      <Trash2 className="w-2.5 h-2.5 text-red-400" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dialog Assegna Turno */}
      <Dialog open={showAssegnaDialog} onOpenChange={setShowAssegnaDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assegna Turno</DialogTitle>
          </DialogHeader>
          {selectedCell && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                <strong>{selectedCell.userName}</strong> - {selectedCell.data}
              </p>
              {selectedCell.assegnazione && (
                <Button variant="outline" className="w-full" onClick={() => {
                  rimuoviTurno(selectedCell.assegnazione.id);
                  setShowAssegnaDialog(false);
                }}>
                  Rimuovi Turno
                </Button>
              )}
              <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                {legenda.map(turno => (
                  <Button 
                    key={turno.id} 
                    variant="outline" 
                    className="flex flex-col items-center justify-center p-3 h-auto min-h-[80px] hover:shadow-sm transition-all rounded-lg"
                    style={{
                      borderColor: turno.colore + '80',
                      backgroundColor: turno.colore + '08'
                    }}
                    onClick={() => assegnaTurno(selectedCell.userId, selectedCell.data, turno.id)}>
                    <div className="w-6 h-6 rounded-full mb-1.5" style={{backgroundColor: turno.colore}} />
                    <span className="text-sm font-bold" style={{color: turno.colore}}>
                      {turno.sigla}
                    </span>
                    <span className="text-[10px] text-gray-500 text-center mt-0.5">
                      {turno.descrizione}
                    </span>
                    {turno.durata_ore > 0 && (
                      <span className="text-[9px] text-gray-400 mt-0.5">
                        {turno.durata_ore}h
                      </span>
                    )}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Modifica Turno */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTurno ? 'Modifica Turno' : 'Nuovo Turno'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTurno} className="space-y-4 mt-4">
            <div>
              <Label>Sigla *</Label>
              <Input value={turnoForm.sigla} onChange={(e) => setTurnoForm({...turnoForm, sigla: e.target.value})}
                placeholder="M" required maxLength={10} />
            </div>
            <div>
              <Label>Descrizione *</Label>
              <Input value={turnoForm.descrizione} onChange={(e) => setTurnoForm({...turnoForm, descrizione: e.target.value})}
                placeholder="Mattina" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Colore</Label>
                <Input type="color" value={turnoForm.colore} 
                  onChange={(e) => setTurnoForm({...turnoForm, colore: e.target.value})} className="h-10" />
              </div>
              <div>
                <Label>Icona</Label>
                <Input value={turnoForm.icona} onChange={(e) => setTurnoForm({...turnoForm, icona: e.target.value})}
                  placeholder="☀️" maxLength={2} className="text-2xl" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Durata (ore)</Label>
                <Input type="number" step="0.5" value={turnoForm.durata_ore}
                  onChange={(e) => setTurnoForm({...turnoForm, durata_ore: parseFloat(e.target.value) || 0})} />
              </div>
              <div>
                <Label>Ordine</Label>
                <Input type="number" value={turnoForm.ordine}
                  onChange={(e) => setTurnoForm({...turnoForm, ordine: parseInt(e.target.value) || 0})} />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white">
                Salva
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annulla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Clona Mese */}
      <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Clona Turni da Mese Precedente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Copia tutti i turni dal mese precedente al mese corrente. I turni clonati saranno in modalità BOZZA.
            </p>
            <Button 
              className="w-full bg-purple-600 hover:bg-purple-700"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const anno_a = currentDate.getFullYear();
                  const mese_a = currentDate.getMonth() + 1;
                  
                  // Calcola mese precedente
                  const prevDate = new Date(currentDate);
                  prevDate.setMonth(prevDate.getMonth() - 1);
                  const anno_da = prevDate.getFullYear();
                  const mese_da = prevDate.getMonth() + 1;
                  
                  const response = await axios.post(
                    `${TURNI_API}/turni/calendario/clone-month`,
                    null,
                    {
                      params: { anno_da, mese_da, anno_a, mese_a },
                      headers: { Authorization: `Bearer ${token}` }
                    }
                  );
                  
                  toast.success(response.data.message);
                  fetchCalendario();
                  setShowCloneDialog(false);
                } catch (error) {
                  const detail = error.response?.data?.detail;
                  if (Array.isArray(detail)) {
                    const errorMsg = detail.map(err => err.msg || err).join(', ');
                    toast.error(`Errore: ${errorMsg}`);
                  } else {
                    toast.error(detail || 'Errore clonazione turni');
                  }
                }
              }}>
              Clona da {new Date(currentDate.getFullYear(), currentDate.getMonth() - 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Compilazione Automatica */}
      <Dialog open={showAutoAssignDialog} onOpenChange={setShowAutoAssignDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Compilazione Automatica Turni</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            const selectedUsers = Array.from(formData.getAll('users'));
            const patternInput = formData.get('pattern');
            const overwrite = formData.get('overwrite') === 'on';
            
            if (!patternInput || patternInput.trim() === '') {
              toast.error('Inserisci un pattern (es: M,P,N,R)');
              return;
            }
            
            const pattern = patternInput.split(',').map(s => s.trim()).filter(s => s);
            
            if (selectedUsers.length === 0) {
              toast.error('Seleziona almeno un operatore');
              return;
            }
            if (pattern.length === 0) {
              toast.error('Pattern non valido. Usa virgola per separare le sigle (es: M,P,N)');
              return;
            }
            
            // Verifica che le sigle del pattern esistano nella legenda
            const legendaSigle = legenda.map(t => t.sigla.toLowerCase());
            const invalidSigle = pattern.filter(p => !legendaSigle.includes(p.toLowerCase()));
            if (invalidSigle.length > 0) {
              toast.error(`Sigle non valide: ${invalidSigle.join(', ')}. Usa le sigle dalla legenda (es: M, P, N, R).`);
              return;
            }
            
            try {
              const token = localStorage.getItem('token');
              const anno = currentDate.getFullYear();
              const mese = currentDate.getMonth() + 1;
              
              const response = await axios.post(
                `${TURNI_API}/turni/calendario/auto-assign`,
                {
                  anno,
                  mese,
                  user_ids: selectedUsers,
                  pattern,
                  overwrite
                },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              // Mostra messaggio dettagliato
              const data = response.data;
              if (data.skipped_count > 0 && data.assigned_count === 0 && data.overwritten_count === 0) {
                toast.warning(data.message, { duration: 5000 });
              } else {
                toast.success(data.message);
              }
              
              fetchCalendario();
              setShowAutoAssignDialog(false);
            } catch (error) {
              const detail = error.response?.data?.detail;
              if (Array.isArray(detail)) {
                const errorMsg = detail.map(err => err.msg || err).join(', ');
                toast.error(`Errore: ${errorMsg}`);
              } else {
                toast.error(detail || 'Errore compilazione automatica');
              }
            }
          }} className="space-y-4">
            <div>
              <Label>Seleziona Operatori</Label>
              <div className="max-h-48 overflow-y-auto border rounded p-3 space-y-2 bg-gray-50">
                {calendarioData.users.filter(u => u.full_name && u.full_name.trim()).map(operatore => (
                  <label key={operatore.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded transition-colors">
                    <input 
                      type="checkbox" 
                      name="users" 
                      value={operatore.id}
                      className="w-4 h-4 text-blue-600" />
                    <span className="text-sm">{operatore.full_name}</span>
                    <span className="text-xs text-gray-500 ml-auto">{operatore.figura_professionale}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {calendarioData.users.filter(u => u.full_name && u.full_name.trim()).length} operatori disponibili
              </p>
            </div>
            
            <div>
              <Label>Pattern Turni (Sigle separate da virgola)</Label>
              <Input 
                name="pattern"
                placeholder="Esempio: M,M,P,P,N,R,R"
                className="font-mono" />
              <p className="text-xs text-gray-500 mt-1">
                Inserisci le SIGLE dei turni dalla lista sotto, separate da virgola. Il pattern si ripeterà per tutto il mese.
              </p>
              <div className="mt-2 p-2 bg-gray-50 rounded-lg border border-gray-200 text-xs space-y-1">
                <strong className="text-gray-700">Sigle disponibili:</strong>
                <div className="font-mono text-[11px] text-gray-600 mt-1">
                  {legenda.map(t => (
                    <span key={t.id} className="inline-flex items-center gap-1 mr-2 mb-1 px-2 py-1 bg-white rounded border border-gray-200">
                      <span className="w-2 h-2 rounded-full" style={{backgroundColor: t.colore}} />
                      <strong>{t.sigla}</strong> <span className="text-gray-400">=</span> {t.descrizione}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm">
              <p className="font-medium mb-1 text-gray-700">Esempio:</p>
              <p className="text-xs text-gray-600">
                <code className="font-mono bg-white px-1.5 py-0.5 rounded border text-cyan-700">M,M,P,P,N,R,R</code> = 
                Mattina x2, Pomeriggio x2, Notte, Riposo x2
              </p>
              <p className="text-[10px] text-gray-400 mt-1">
                Il pattern si ripete ciclicamente per tutti i giorni del mese
              </p>
            </div>
            
            <label className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
              <input 
                type="checkbox" 
                name="overwrite" 
                id="overwrite-checkbox"
                className="w-4 h-4 text-amber-600 rounded" />
              <div>
                <span className="text-sm font-medium text-gray-700">Sovrascrivi turni esistenti</span>
                <span className="text-[11px] block text-gray-500 mt-0.5">
                  Se attivo, sostituisce i turni già assegnati per gli operatori selezionati
                </span>
              </div>
            </label>
            
            <div className="flex gap-2">
              <Button type="submit" className="flex-1 bg-cyan-600 hover:bg-cyan-700">
                Genera Turni
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowAutoAssignDialog(false)}>
                Annulla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Richiesta Assenza */}
      <Dialog open={showRichiestaDialog} onOpenChange={setShowRichiestaDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Richiedi Assenza</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            
            // Previeni doppio invio
            if (isSubmittingRichiesta) return;
            setIsSubmittingRichiesta(true);
            
            try {
              const token = localStorage.getItem('token');
              
              // Prepara i dati: converti ore vuoto in null e assicurati sia intero
              const dataToSend = {
                ...richiestaForm,
                ore: richiestaForm.ore ? parseInt(richiestaForm.ore, 10) : null,
                user_id: user.id  // Aggiungi user_id
              };
              
              const response = await axios.post(
                `${TURNI_API}/richieste-assenze`,
                dataToSend,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              // Verifica che la risposta sia valida
              if (response.status === 200 || response.status === 201) {
                toast.success('Richiesta inviata al coordinatore');
                setShowRichiestaDialog(false);
                setRichiestaForm({ tipo: 'ferie', data_inizio: '', data_fine: '', ore: '', motivo: '' });
                // Ricarica le richieste
                fetchRichieste();
              } else {
                toast.error('Errore: risposta non valida dal server');
              }
            } catch (error) {
              const detail = error.response?.data?.detail;
              if (Array.isArray(detail)) {
                // Errore di validazione Pydantic
                const errorMsg = detail.map(err => err.msg || err).join(', ');
                toast.error(`Errore validazione: ${errorMsg}`);
              } else if (typeof detail === 'string') {
                toast.error(detail);
              } else if (error.message) {
                // Mostra messaggio di errore specifico
                toast.error(`Errore: ${error.message}`);
              } else {
                toast.error('Errore invio richiesta. Riprova.');
              }
            } finally {
              setIsSubmittingRichiesta(false);
            }
          }} className="space-y-4">
            {/* Tipo Assenza */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-600">Tipo Assenza</Label>
              <select 
                value={richiestaForm.tipo}
                onChange={(e) => setRichiestaForm({...richiestaForm, tipo: e.target.value})}
                className="w-full h-9 px-3 rounded-md border border-gray-200 bg-white text-sm text-gray-900"
                required>
                <option value="malattia">Malattia</option>
                <option value="ferie">Ferie</option>
                <option value="permesso">Permesso</option>
                <option value="permesso_l104">Permesso L.104</option>
                <option value="recupero">Recupero Ore</option>
                <option value="donazione_sangue">Donazione Sangue</option>
              </select>
            </div>
            
            {/* Date */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-600">Data Inizio</Label>
                <Input 
                  type="date" 
                  value={richiestaForm.data_inizio}
                  onChange={(e) => setRichiestaForm({...richiestaForm, data_inizio: e.target.value})}
                  className="h-9"
                  required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-600">Data Fine</Label>
                <Input 
                  type="date" 
                  value={richiestaForm.data_fine}
                  onChange={(e) => setRichiestaForm({...richiestaForm, data_fine: e.target.value})}
                  className="h-9"
                  required />
              </div>
            </div>
            
            {/* Campo Ore - per permessi e recupero */}
            {(richiestaForm.tipo === 'permesso' || richiestaForm.tipo === 'permesso_l104' || richiestaForm.tipo === 'recupero') && (
              <div className="space-y-1.5 bg-gray-50 p-3 rounded-lg border border-gray-200">
                <Label className="text-xs font-medium text-gray-600">
                  Ore {richiestaForm.tipo === 'recupero' ? 'da Recuperare' : 'Permesso'}
                  <span className="text-[10px] text-gray-400 ml-1">(opzionale se giornata intera)</span>
                </Label>
                <Input 
                  type="number" 
                  min="0.5"
                  max="24"
                  step="0.5"
                  value={richiestaForm.ore}
                  onChange={(e) => setRichiestaForm({...richiestaForm, ore: e.target.value})}
                  className="h-9"
                  placeholder="Es: 4" />
              </div>
            )}
            
            {/* Motivo */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-600">Motivo / Note</Label>
              <textarea 
                value={richiestaForm.motivo}
                onChange={(e) => setRichiestaForm({...richiestaForm, motivo: e.target.value})}
                className="w-full h-20 px-3 py-2 rounded-md border border-gray-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                placeholder="Descrivi brevemente il motivo della richiesta..."
                required />
            </div>
            
            {/* Submit */}
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setShowRichiestaDialog(false)} className="flex-1" disabled={isSubmittingRichiesta}>
                Annulla
              </Button>
              <Button type="submit" className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white" disabled={isSubmittingRichiesta}>
                {isSubmittingRichiesta ? 'Invio...' : 'Invia Richiesta'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Gestione Richieste Assenze */}
      <Dialog open={showRichiesteDialog} onOpenChange={(open) => {
        setShowRichiesteDialog(open);
        if (open) markPendingAsViewed();
      }}>
        <DialogContent className="max-w-4xl max-h-[85vh] sm:max-h-[80vh] overflow-y-auto p-4 sm:p-6 w-[95vw] sm:w-full">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-lg sm:text-xl">Richieste Assenze</DialogTitle>
          </DialogHeader>
          
          {/* Filtri per stato richieste */}
          <div className="flex flex-wrap gap-1.5 mb-3 pb-3 border-b border-gray-100">
            {[
              { key: 'pending', label: 'Da Approvare', color: 'bg-amber-100 text-amber-700 border-amber-200' },
              { key: 'approved', label: 'Approvate', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
              { key: 'rejected', label: 'Rifiutate', color: 'bg-red-100 text-red-700 border-red-200' },
              { key: 'all', label: 'Tutte', color: 'bg-gray-100 text-gray-700 border-gray-200' }
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFiltroStatoRichieste(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  filtroStatoRichieste === f.key ? f.color + ' shadow-sm' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                }`}>
                {f.label} ({f.key === 'all' ? richieste.length : richieste.filter(r => r.stato === f.key).length})
              </button>
            ))}
          </div>
          
          <div className="space-y-3">
            {(() => {
              const richiesteFiltrate = filtroStatoRichieste === 'all' 
                ? richieste 
                : richieste.filter(r => r.stato === filtroStatoRichieste);
              
              return richiesteFiltrate.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  {filtroStatoRichieste === 'pending' && 'Nessuna richiesta da approvare'}
                  {filtroStatoRichieste === 'approved' && 'Nessuna richiesta approvata'}
                  {filtroStatoRichieste === 'rejected' && 'Nessuna richiesta rifiutata'}
                  {filtroStatoRichieste === 'all' && 'Nessuna richiesta presente'}
                </p>
              ) : (
                richiesteFiltrate.map(richiesta => {
                const user_richiedente = calendarioData.users.find(u => u.id === richiesta.user_id);
                const statusColors = {
                  pending: 'border-amber-200 bg-amber-50/50',
                  approved: 'border-emerald-200 bg-emerald-50/50',
                  rejected: 'border-red-200 bg-red-50/50'
                };
                const statusLabels = {
                  pending: 'In Attesa',
                  approved: 'Approvata',
                  rejected: 'Rifiutata'
                };
                const statusBadge = {
                  pending: 'bg-amber-100 text-amber-700',
                  approved: 'bg-emerald-100 text-emerald-700',
                  rejected: 'bg-red-100 text-red-700'
                };
                
                return (
                  <Card key={richiesta.id} className={`border ${statusColors[richiesta.stato]}`}>
                    <CardContent className="p-3">
                      <div className="flex flex-col sm:flex-row justify-between items-start gap-2">
                        <div className="flex-1 w-full">
                          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                            <span className="font-semibold text-sm text-gray-900">
                              {user_richiedente?.full_name || 'Utente sconosciuto'}
                            </span>
                            <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${statusBadge[richiesta.stato]}`}>
                              {statusLabels[richiesta.stato]}
                            </span>
                            <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-medium text-gray-600 uppercase">
                              {richiesta.tipo}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-600">
                            <span>Dal: <strong>{new Date(richiesta.data_inizio).toLocaleDateString('it-IT')}</strong></span>
                            <span>Al: <strong>{new Date(richiesta.data_fine).toLocaleDateString('it-IT')}</strong></span>
                            {richiesta.ore && <span>Ore: <strong>{richiesta.ore}</strong></span>}
                          </div>
                          <p className="text-xs text-gray-600 mt-1"><strong>Motivo:</strong> {richiesta.motivo}</p>
                          {richiesta.nota_coordinatore && (
                            <div className="mt-1.5 p-2 bg-cyan-50 rounded-md border border-cyan-100">
                              <p className="text-[11px] text-cyan-800"><strong>Nota coordinatore:</strong> {richiesta.nota_coordinatore}</p>
                            </div>
                          )}
                        </div>
                        
                        {richiesta.stato === 'pending' && (user.is_coordinatore || user.role === 'super_amministratore') && (
                          <div className="flex gap-1.5 w-full sm:w-auto mt-2 sm:mt-0 sm:ml-3 shrink-0">
                            <Button 
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 flex-1 sm:flex-initial gap-1"
                              onClick={async () => {
                                const nota = prompt('Nota (opzionale):') || '';
                                try {
                                  const token = localStorage.getItem('token');
                                  await axios.put(
                                    `${TURNI_API}/richieste-assenze/${richiesta.id}/approva`,
                                    null,
                                    {
                                      params: { nota },
                                      headers: { Authorization: `Bearer ${token}` }
                                    }
                                  );
                                  toast.success('Richiesta approvata - Calendario sincronizzato');
                                  fetchRichieste();
                                  fetchCalendario(); // Ricarica il calendario per mostrare le assenze
                                } catch (error) {
                                  const detail = error.response?.data?.detail;
                                  if (Array.isArray(detail)) {
                                    const errorMsg = detail.map(err => err.msg || err).join(', ');
                                    toast.error(`Errore: ${errorMsg}`);
                                  } else {
                                    toast.error(detail || 'Errore approvazione');
                                  }
                                }
                              }}>
                              Approva
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              className="text-xs h-8 flex-1 sm:flex-initial gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                              onClick={async () => {
                                const nota = prompt('Motivo rifiuto (obbligatorio):');
                                if (!nota) {
                                  toast.error('Inserisci un motivo');
                                  return;
                                }
                                try {
                                  const token = localStorage.getItem('token');
                                  await axios.put(
                                    `${TURNI_API}/richieste-assenze/${richiesta.id}/rifiuta`,
                                    null,
                                    {
                                      params: { nota },
                                      headers: { Authorization: `Bearer ${token}` }
                                    }
                                  );
                                  toast.success('Richiesta rifiutata');
                                  fetchRichieste();
                                } catch (error) {
                                  const detail = error.response?.data?.detail;
                                  if (Array.isArray(detail)) {
                                    const errorMsg = detail.map(err => err.msg || err).join(', ');
                                    toast.error(`Errore: ${errorMsg}`);
                                  } else {
                                    toast.error(detail || 'Errore rifiuto');
                                  }
                                }
                              }}>
                              Rifiuta
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Report Assenze (Excel + PDF) — Super Admin only */}
      <Dialog open={showReportAssenzeDialog} onOpenChange={setShowReportAssenzeDialog}>
        <DialogContent className="sm:max-w-md" data-testid="report-assenze-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileText className="w-4 h-4 text-cyan-600" />
              Report Assenze
            </DialogTitle>
            <DialogDescription className="text-xs">
              Esporta riepilogo aggregato + dettaglio analitico. Sono inclusi <strong>solo gli operatori con almeno un giorno di assenza</strong> nel periodo selezionato.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Anno</Label>
                <Input
                  type="number" min="2020" max="2099"
                  value={reportAssenzeAnno}
                  onChange={(e) => setReportAssenzeAnno(parseInt(e.target.value, 10) || new Date().getFullYear())}
                  className="h-9"
                  data-testid="report-assenze-anno"
                />
              </div>
              <div>
                <Label className="text-xs">Mese (opzionale)</Label>
                <select
                  value={reportAssenzeMese}
                  onChange={(e) => setReportAssenzeMese(e.target.value)}
                  className="w-full h-9 px-2 text-sm rounded-md border border-gray-300 bg-white"
                  data-testid="report-assenze-mese"
                >
                  <option value="">Tutto l&apos;anno</option>
                  {[
                    'Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno',
                    'Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'
                  ].map((nm, idx) => (
                    <option key={idx + 1} value={idx + 1}>{idx + 1} · {nm}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs">ASL (opzionale)</Label>
              <select
                value={reportAssenzeAsl}
                onChange={(e) => setReportAssenzeAsl(e.target.value)}
                className="w-full h-9 px-2 text-sm rounded-md border border-gray-300 bg-white"
                data-testid="report-assenze-asl"
              >
                <option value="">Tutte le ASL</option>
                {(listaASL || []).map(a => {
                  const value = typeof a === 'string' ? a : (a?.nome || a?.id || '');
                  if (!value) return null;
                  const clean = value.replace(/^asl\s+/i, '');
                  return <option key={value} value={value}>ASL {clean}</option>;
                })}
              </select>
            </div>
            <p className="text-[11px] text-gray-500">
              Periodo selezionato: <strong>{reportAssenzeMese ? `${String(reportAssenzeMese).padStart(2,'0')}/${reportAssenzeAnno}` : `tutto il ${reportAssenzeAnno}`}</strong>
              {reportAssenzeAsl && <> · ASL: <strong>{reportAssenzeAsl}</strong></>}
            </p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              disabled={reportAssenzeLoading}
              onClick={() => setShowReportAssenzeDialog(false)}
              className="h-9 text-xs"
            >Annulla</Button>
            <Button
              disabled={reportAssenzeLoading}
              onClick={async () => {
                setReportAssenzeLoading(true);
                try {
                  const token = localStorage.getItem('token');
                  const qs = `?anno=${reportAssenzeAnno}${reportAssenzeMese ? `&mese=${reportAssenzeMese}` : ''}${reportAssenzeAsl ? `&asl=${encodeURIComponent(reportAssenzeAsl)}` : ''}`;
                  const res = await axios.get(`${TURNI_API}/turni/report-assenze/excel${qs}`, {
                    responseType: 'blob',
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement('a');
                  a.href = url;
                  const aslSlug = reportAssenzeAsl ? `_${reportAssenzeAsl.replace(/\s+/g, '-').toLowerCase()}` : '';
                  a.download = `report-assenze_${reportAssenzeAnno}${reportAssenzeMese ? `-${String(reportAssenzeMese).padStart(2,'0')}` : ''}${aslSlug}.xlsx`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                  toast.success('Report Excel scaricato');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Errore download Excel');
                } finally {
                  setReportAssenzeLoading(false);
                }
              }}
              className="h-9 text-xs bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
              data-testid="report-assenze-download-excel"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              Excel
            </Button>
            <Button
              disabled={reportAssenzeLoading}
              onClick={async () => {
                setReportAssenzeLoading(true);
                try {
                  const token = localStorage.getItem('token');
                  const qs = `?anno=${reportAssenzeAnno}${reportAssenzeMese ? `&mese=${reportAssenzeMese}` : ''}${reportAssenzeAsl ? `&asl=${encodeURIComponent(reportAssenzeAsl)}` : ''}`;
                  const res = await axios.get(`${TURNI_API}/turni/report-assenze/pdf${qs}`, {
                    responseType: 'blob',
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement('a');
                  a.href = url;
                  const aslSlug = reportAssenzeAsl ? `_${reportAssenzeAsl.replace(/\s+/g, '-').toLowerCase()}` : '';
                  a.download = `report-assenze_${reportAssenzeAnno}${reportAssenzeMese ? `-${String(reportAssenzeMese).padStart(2,'0')}` : ''}${aslSlug}.pdf`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                  toast.success('Report PDF scaricato');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Errore download PDF');
                } finally {
                  setReportAssenzeLoading(false);
                }
              }}
              className="h-9 text-xs bg-cyan-600 hover:bg-cyan-700 text-white gap-1.5"
              data-testid="report-assenze-download-pdf"
            >
              <FileText className="w-3.5 h-3.5" />
              PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}